import { InjectionToken, Provider } from '@angular/core';

/**
 * Configuration interface for the AI Onboarding module.
 *
 * Optional settings:
 * - googleMapsApiKey: For address validation and autocomplete
 * - defaultProMode: Enable detailed charge extraction by default
 * - maxFileSizeMb: Maximum upload file size (default: 10MB)
 * - allowedFileTypes: Accepted MIME types for upload
 *
 * The AI processing endpoint is configured via environment.apiBaseUrl + '/v4/ai/onboarding'
 */
export interface AiOnboardingConfig {

  /** Optional: Google Maps API key for address validation */
  googleMapsApiKey?: string;

  /** Optional: Enable pro mode (detailed charge extraction) by default */
  defaultProMode?: boolean;

  /** Optional: Maximum file size in MB (default: 10) */
  maxFileSizeMb?: number;

  /** Optional: Allowed MIME types for upload */
  allowedFileTypes?: string[];

  /** Optional: Pre-populated entity data for matching */
  customEntityData?: Array<{
    id: string;
    name: string;
    subLabel?: string;
  }>;

  /** Optional: Pre-populated provider data for matching */
  customProviderData?: Array<{
    id: string;
    name: string;
    subLabel?: string;
  }>;

  /** Optional: Pre-populated address data for matching */
  customAddressData?: Array<{
    id: string;
    street1: string;
    city: string;
    state: string;
    zip: string;
  }>;
}

/** Injection token for the AI Onboarding configuration */
export const AI_ONBOARDING_CONFIG = new InjectionToken<AiOnboardingConfig>('AI_ONBOARDING_CONFIG');

/**
 * Provides the AI Onboarding module configuration and services.
 *
 * Usage in app.config.ts:
 * ```typescript
 * import { provideAiOnboarding } from './ai-onboarding';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideAiOnboarding({
 *       googleMapsApiKey: 'YOUR_API_KEY',
 *       defaultProMode: true
 *     })
 *   ]
 * };
 * ```
 */
export function provideAiOnboarding(config?: Partial<AiOnboardingConfig>): Provider[] {
  const finalConfig = { ...DEFAULT_AI_ONBOARDING_CONFIG, ...config };
  return [
    { provide: AI_ONBOARDING_CONFIG, useValue: finalConfig }
  ];
}

/** Default configuration values */
export const DEFAULT_AI_ONBOARDING_CONFIG: Partial<AiOnboardingConfig> = {
  defaultProMode: false,
  maxFileSizeMb: 10,
  allowedFileTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/webp']
};
